import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Form } from "react-bootstrap";
import { defineMessages, FormattedMessage } from "react-intl";
import styled from "styled-components";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { useKeycloakAuthz } from "../../components/keycloak-provider";
import { SectionHeading } from "../../components/section-heading";
import Selector from "../../components/selector";
import { useUserPropertyTickers } from "../../components/user-tickers-providers";
import { ACCESS_DENIED, getPropertyRpt } from "../../lib/auth";
import { useApiClient } from "../../lib/http";
import { regenPropertyCacheSync } from "../common/api";
import { defaultFormMessages } from "../shared/crud/form";
import { defaultListMessages } from "../shared/crud/list";
import { regenPropertyCalendar, regenPropertyInventoryCalendar } from "./api";

const LoaderWrapper = styled.div`
  width: 100%;
  height: 100%;
  font-size: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 100%;
  color: #cccccc;
  opacity: 0.6;
  background: white;
  z-index: 1;
`;

export function Loader({ children }: { children: React.ReactNode }) {
  return (
    <LoaderWrapper>
      <FontAwesomeIcon icon={faCircleNotch} spin />
      {children}
    </LoaderWrapper>
  );
}

const messages = {
  ...defaultFormMessages,
  ...defaultListMessages,
  ...defineMessages({
    title: {
      id: "internalmenu.utilities.propertyCalendarRegen",
      defaultMessage: "Regen property calendar",
    },
    propertyTickerSelectPlaceholder: {
      id: "channelManager.placeholder.propertyTicker",
      defaultMessage: "Engine identifier",
    },
    bulkLabel: {
      id: "internalmenu.utilities.propertyCalendarRegen.bulkLabel",
      defaultMessage: "Bulk regen",
    },
    bulkHelp: {
      id: "internalmenu.utilities.propertyCalendarRegen.bulkHelp",
      defaultMessage:
        "Enter the engine identifiers separated by commas. They will be processed 2 at a time in parallel.",
    },
    bulkPlaceholder: {
      id: "internalmenu.utilities.propertyCalendarRegen.bulkPlaceholder",
      defaultMessage: "engine1, engine2, engine3",
    },
    bulkRun: {
      id: "internalmenu.utilities.propertyCalendarRegen.bulkRun",
      defaultMessage: "Run bulk regen",
    },
  }),
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1rem;
  > * {
    margin: 1rem 0;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 1rem;
`;
const Label = styled.div`
  font-size: 18px;
  margin-bottom: -10px;
`;

type Option = { value: string; label: string };

interface OperationError {
  message: string;
}

type BulkStatus = "PENDING" | "OK" | "ERROR";

const BULK_PARALLELISM = 2;

function parseBulkInput(raw: string): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const chunk of raw.split(/[,\s]+/)) {
    const trimmed = chunk.trim();
    if (trimmed && !seen.has(trimmed)) {
      seen.add(trimmed);
      result.push(trimmed);
    }
  }
  return result;
}

function PropertyCalendarRegen() {
  const userPropertyTickersList = useUserPropertyTickers();
  const httpClient = useApiClient({ usePropertyHeader: false });
  const keycloakAuthz = useKeycloakAuthz();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<OperationError | null | undefined>(undefined);
  const [operations, setOperations] = useState<string[]>([
    "regenPropertyCache",
    "regenPropertyInventoryCalendar",
    "regenPropertyCalendar",
  ]);
  const [configSuccess, setConfigSuccess] = useState<boolean>(false);

  const [propertyTickerOptions, setPropertyTickerOptions] = useState<Option[]>([]);
  const [propertyTickerOption, setPropertyTickerOption] = useState<Option | null | undefined>(
    undefined
  );

  const [bulkInput, setBulkInput] = useState<string>("");
  const [bulkStatuses, setBulkStatuses] = useState<Record<string, BulkStatus>>({});
  const [bulkEngines, setBulkEngines] = useState<string[]>([]);
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);

  const parsedBulkEngines = useMemo(() => parseBulkInput(bulkInput), [bulkInput]);

  useEffect(() => {
    setPropertyTickerOptions(
      userPropertyTickersList.map(option => ({ value: option, label: option }))
    );
  }, [userPropertyTickersList]);

  const handleSelectPropertyTicker = useCallback(
    (selectedOption: Option | null | undefined) => setPropertyTickerOption(selectedOption),
    [setPropertyTickerOption]
  );

  const regenCalendar = useCallback(async () => {
    if (!propertyTickerOption || !propertyTickerOption.value) {
      return;
    }
    setLoading(true);
    setError(undefined);
    setConfigSuccess(false);
    setOperations([]);
    try {
      const token = await getPropertyRpt(propertyTickerOption.value, keycloakAuthz);
      await regenPropertyCacheSync(httpClient, propertyTickerOption.value, true, token);
      console.log("regenPropertyCache DONE");
      setOperations(["regenPropertyCache"]);
      await regenPropertyInventoryCalendar(httpClient, propertyTickerOption.value, token);
      console.log("regenPropertyInventoryCalendar DONE");
      setOperations(["regenPropertyCache", "regenPropertyInventoryCalendar"]);
      await regenPropertyCalendar(httpClient, propertyTickerOption.value, token);
      console.log("regenPropertyCalendar DONE");
      setOperations([
        "regenPropertyCache",
        "regenPropertyInventoryCalendar",
        "regenPropertyCalendar",
      ]);
      setConfigSuccess(true);
    } catch (e) {
      setConfigSuccess(false);
      if (e === ACCESS_DENIED) {
        console.error("Access denied", e);
        setError({ message: "Access denied" });
      } else {
        console.error("Error retrieving children", e);
        setError({ message: "Error creating chain" });
      }
    }
    setLoading(false);
  }, [httpClient, keycloakAuthz, propertyTickerOption]);

  const regenOneEngine = useCallback(
    async (propertyTicker: string): Promise<void> => {
      const token = await getPropertyRpt(propertyTicker, keycloakAuthz);
      await regenPropertyCacheSync(httpClient, propertyTicker, true, token);
      await regenPropertyInventoryCalendar(httpClient, propertyTicker, token);
      await regenPropertyCalendar(httpClient, propertyTicker, token);
    },
    [httpClient, keycloakAuthz]
  );

  const regenCalendarBulk = useCallback(async () => {
    const engines = parsedBulkEngines;
    if (engines.length === 0) {
      return;
    }
    setBulkLoading(true);
    setBulkEngines(engines);
    setBulkStatuses(
      engines.reduce<Record<string, BulkStatus>>((acc, engine) => {
        acc[engine] = "PENDING";
        return acc;
      }, {})
    );

    for (let i = 0; i < engines.length; i += BULK_PARALLELISM) {
      const chunk = engines.slice(i, i + BULK_PARALLELISM);
      await Promise.all(
        chunk.map(async engine => {
          try {
            await regenOneEngine(engine);
            setBulkStatuses(prev => ({ ...prev, [engine]: "OK" }));
          } catch (e) {
            console.error(`Bulk regen failed for ${engine}`, e);
            setBulkStatuses(prev => ({ ...prev, [engine]: "ERROR" }));
          }
        })
      );
    }

    setBulkLoading(false);
  }, [parsedBulkEngines, regenOneEngine]);

  return (
    <>
      <SectionHeading>
        <FormattedMessage {...messages.title} />
      </SectionHeading>
      {loading ? (
        <Loader>
          {operations.map(it => (
            <div style={{ fontSize: 14, color: "black" }}>OK: {it}</div>
          ))}
        </Loader>
      ) : null}
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <Container>
          <Label>
            <FormattedMessage {...messages.propertyTickerSelectPlaceholder} />
          </Label>
          <Selector
            options={propertyTickerOptions}
            value={propertyTickerOption}
            onChange={handleSelectPropertyTicker}
            placeholder={<FormattedMessage {...messages.propertyTickerSelectPlaceholder} />}
          />
        </Container>
        {error && (
          <Alert variant="danger">
            <strong>
              <FormattedMessage {...messages.saveError} />
            </strong>
          </Alert>
        )}
        {configSuccess && (
          <Alert variant="success">
            <strong>
              <FormattedMessage {...messages.saveSuccess} />
            </strong>
          </Alert>
        )}
        <ButtonContainer>
          <Button variant="primary" type="button" onClick={regenCalendar}>
            <FormattedMessage {...messages.save} />
          </Button>
        </ButtonContainer>
      </Card>
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <Container>
          <Label>
            <FormattedMessage {...messages.bulkLabel} />
          </Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={bulkInput}
            onChange={e => setBulkInput(e.target.value)}
            placeholder="engine1, engine2, engine3"
            disabled={bulkLoading}
          />
          <Form.Text className="text-muted">
            <FormattedMessage {...messages.bulkHelp} />
          </Form.Text>
          {bulkEngines.length > 0 && (
            <div>
              {bulkEngines.map(engine => {
                const status = bulkStatuses[engine] ?? "PENDING";
                const color = status === "OK" ? "green" : status === "ERROR" ? "crimson" : "#888";
                return (
                  <div key={engine} style={{ fontSize: 14, color }}>
                    {status}: {engine}
                  </div>
                );
              })}
            </div>
          )}
        </Container>
        <ButtonContainer>
          <Button
            variant="primary"
            type="button"
            onClick={regenCalendarBulk}
            disabled={bulkLoading || parsedBulkEngines.length === 0}>
            {bulkLoading ? (
              <FontAwesomeIcon icon={faCircleNotch} spin />
            ) : (
              <FormattedMessage {...messages.bulkRun} />
            )}
          </Button>
        </ButtonContainer>
      </Card>
    </>
  );
}

export default withAuthorizationVerifier(PropertyCalendarRegen);
